import type { EmbedReference } from '@shared/ast/astTypes'
import { serializeEmbedReference } from '@shared/ast/embedReference'
import { useEffect, useState } from 'react'

import { type ResolvedEmbed, apiService } from '../../../services/api'

/** Source prose belongs only to the view, never the persisted editor state. */
export function useSourceEmbed(bookId: string | undefined, source: EmbedReference) {
  const [result, setResult] = useState<ResolvedEmbed | null>(null)
  const [failed, setFailed] = useState(false)
  const [loadedFor, setLoadedFor] = useState<string | null>(null)
  const referenceKey = serializeEmbedReference(source)
  const requestKey = `${bookId ?? ''}:${referenceKey}`

  useEffect(() => {
    let active = true
    let requestId = 0
    setResult(null)
    setLoadedFor(null)
    setFailed(false)
    const refresh = async () => {
      if (!bookId) {
        return
      }
      const current = ++requestId
      try {
        const response = await apiService.resolveEmbed(bookId, source)
        if (active && current === requestId) {
          setResult(response.data || null)
          setLoadedFor(requestKey)
          setFailed(!response.success || !response.data)
        }
      } catch {
        if (active && current === requestId) {
          setResult(null)
          setLoadedFor(requestKey)
          setFailed(true)
        }
      }
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void refresh()
      }
    }
    const onSaved = (event: Event) => {
      if ((event as CustomEvent<{ bookId: string }>).detail?.bookId === bookId) {
        void refresh()
      }
    }
    void refresh()
    window.addEventListener('focus', refresh)
    window.addEventListener('manifesto:content-saved', onSaved)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      active = false
      window.removeEventListener('focus', refresh)
      window.removeEventListener('manifesto:content-saved', onSaved)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [requestKey])

  return { result: loadedFor === requestKey ? result : null, failed: loadedFor === requestKey && failed }
}
